<template>
  <template v-if="!hideContactFields">
    <div class="grid grid-cols-2 gap-4">
      <div class="el-form-validation-margin mt-2">
        <label class="required form-label" for="name">{{
          $t('subscribeToEventUpdatesModal.firstStep.form.name.label')
        }}</label>
        <div class="position-relative">
          <input
            id="name"
            ref="firstName"
            v-model="form.name"
            :placeholder="
              $t(
                'subscribeToEventUpdatesModal.firstStep.form.name.placeholder'
              )
            "
            autocomplete="name"
            class="w-full border border-gray-200 rounded-lg p-2 bg-white mt-1"
            name="name"
            @input="emit('clearErrors', 'name')"
          />
        </div>
        <span
          v-if="invalidKeys.includes('name')"
          data-test-id="date-request-form-invalid"
          class="text-red text-sm"
        >
          {{ errors.name }}
        </span>
      </div>
      <div class="el-form-validation-margin mt-2">
        <label class="required form-label" for="surname">{{
          $t('subscribeToEventUpdatesModal.firstStep.form.surname.label')
        }}</label>
        <div class="position-relative">
          <input
            id="surname"
            v-model="form.surname"
            :placeholder="
              $t(
                'subscribeToEventUpdatesModal.firstStep.form.surname.placeholder'
              )
            "
            autocomplete="surname"
            class="w-full border border-gray-200 rounded-lg p-2 bg-white mt-1"
            name="surname"
            @input="emit('clearErrors', 'surname')"
          />
        </div>
        <span
          v-if="invalidKeys.includes('surname')"
          data-test-id="date-request-form-invalid"
          class="text-red text-sm"
        >
          {{ errors.surname }}
        </span>
      </div>
    </div>
    <div class="el-form-validation-margin mt-2">
      <label class="required form-label" for="email">{{
        $t('subscribeToEventUpdatesModal.firstStep.form.email.label')
      }}</label>
      <div class="position-relative">
        <input
          id="email"
          v-model="form.email"
          :placeholder="
            $t(
              'subscribeToEventUpdatesModal.firstStep.form.email.placeholder'
            )
          "
          autocomplete="email"
          type="email"
          class="w-full border border-gray-200 rounded-lg p-2 bg-white mt-1"
          name="email"
          @input="emit('clearErrors', 'email')"
        />
      </div>
      <span
        v-if="invalidKeys.includes('email')"
        data-test-id="date-request-form-invalid"
        class="text-red text-sm"
      >
        {{ errors.email }}
      </span>
    </div>
    <div class="el-form-validation-margin mt-2">
      <label
        class="form-label"
        for="phone"
        :class="{ required: isPrivateEventRequest }"
        >{{
          $t('subscribeToEventUpdatesModal.firstStep.form.phone.label')
        }}</label
      >
      <div class="position-relative">
        <input
          id="phone"
          v-model="form.phone"
          :placeholder="
            $t(
              'subscribeToEventUpdatesModal.firstStep.form.phone.placeholder'
            )
          "
          autocomplete="phone"
          class="w-full border border-gray-200 rounded-lg p-2 bg-white mt-1"
          name="phone"
          type="tel"
          @input="emit('clearErrors', 'phone')"
        />
      </div>
      <span
        v-if="invalidKeys.includes('phone')"
        data-test-id="date-request-form-invalid"
        class="text-red text-sm"
      >
        {{ errors.phone }}
      </span>
      <span v-else class="block mb-2 text-xs text-gray-400">
        {{
          $t(
            'subscribeToEventUpdatesModal.firstStep.form.phone.helperText'
          )
        }}
      </span>
    </div>
  </template>
</template>
<script lang="ts" setup>
import type {
  DateRequestErrors,
  DateRequestInvalidKeys,
} from '~/components/date-request/dateRequestTypes'
import type { EventDateType } from '~/composables/types/event'

interface DateRequestFormModel {
  email: string
  phone: string
  name: string
  surname: string
  numberOfTickets: number | null
  companyName: string
  city: string
  dateType: EventDateType | null
  dates: string[]
  additionalInfo?: string
}

interface Props {
  isPrivateEventRequest?: boolean
  errors?: DateRequestErrors
  invalidKeys?: DateRequestInvalidKeys
  hideContactFields?: boolean
}
const {
  isPrivateEventRequest = true,
  errors = {},
  invalidKeys = [],
  hideContactFields = false,
} = defineProps<Props>()
const emit = defineEmits<{
  (e: 'clearErrors', id: string): void
}>()
const form = defineModel<DateRequestFormModel>()
const firstName = ref<HTMLInputElement | null>(null)

const { isMobile } = useDevice()
const focusFirstInput = () => {
  if (!hideContactFields && firstName.value && !isMobile) {
    firstName.value.focus()
  }
}
onMounted(focusFirstInput)

watch(
  () => hideContactFields,
  () => {
    nextTick(() => focusFirstInput())
  }
)
</script>
<style>
.required:after {
  content: '*';
  position: relative;
  font-size: inherit;
  @apply text-red;
  padding-left: 0.25rem;
  font-weight: 700;
}
</style>
